import { useCallback, useEffect, useRef } from 'react';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';
import { RISK_INVALIDATION_CAUSES } from '../orchestration/useRiskOrchestrationCommands';

export default function useImportApplySync({
    userUid,
    requestPosSizingContextUpdate,
    actualPortfolioVersion,
    setActualPortfolioVersion,
    riskCommandsRef,
    markPendingRefreshFromLoadResult,
    autoRefreshEnabledRef,
    refreshPriceDataRef,
    importApplyInFlightRef,
    posSizingSyncInFlightRef,
}) {
    const mountedScopeRef = useRef(null);
    const applyHandlerRef = useRef(null);
    const handleImportApplied = useCallback(async () => {
        const scope = mountedScopeRef.current;
        const isCurrent = () => scope !== null && mountedScopeRef.current === scope;
        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'useImportApplySync.handleImportApplied',
            [
                'invalidateRiskSurface',
                'refreshRiskSurface',
                'refreshSelectedOverview',
            ],
        );
        // The import owns the selected Overview while it runs (including any
        // Position Sizing sync it starts) and refreshes it once at the end.
        importApplyInFlightRef.current = true;
        try {
            const pendingSync = posSizingSyncInFlightRef?.current;
            if (pendingSync && typeof pendingSync.then === 'function') {
                await pendingSync.catch(() => {});
            }
            if (!isCurrent()) {
                throw new Error('Actual portfolio was saved, but its workspace changed before Position Sizing could be synced. Reload the original account.');
            }
            const nextActualPortfolioVersion = actualPortfolioVersion + 1;
            setActualPortfolioVersion(nextActualPortfolioVersion);
            commands.invalidateRiskSurface({
                cause: RISK_INVALIDATION_CAUSES.ACTUAL_PORTFOLIO_CHANGED,
                reason: 'portfolio_import',
            });
            const loadResult = await commands.refreshRiskSurface({
                    reason: 'import',
                    positionsVersion: nextActualPortfolioVersion,
                    includeOptimizerSummaries: false,
                });
            if (!isCurrent()) {
                throw new Error('Actual portfolio was saved, but its workspace changed before Position Sizing could be synced. Reload the original account.');
            }
            const hasPendingRefresh = markPendingRefreshFromLoadResult(loadResult);
            if (hasPendingRefresh && autoRefreshEnabledRef.current) {
                // Price work remains inside this import owner. Its completion
                // precedes the single authorized child handoff below.
                await refreshPriceDataRef.current?.({
                    reason: 'import_price_refresh',
                    syncPosSizingDraft: false,
                });
            }

            commands.invalidateRiskSurface({
                cause: RISK_INVALIDATION_CAUSES.POS_SIZING_RESULT_CHANGED,
                reason: 'portfolio_import',
            });
            const result = await requestPosSizingContextUpdate({
                reason: 'portfolio_import', acceptance: 'actual_change',
                allowParentPublication: true,
                matricesOverride: hasPendingRefresh && autoRefreshEnabledRef.current
                    ? null : loadResult?.matricesOverride ?? null,
                isCurrentRequest: isCurrent,
            });
            if (!result?.success) {
                throw new Error(result?.message || 'Actual portfolio was saved, but Position Sizing still needs to be synced.');
            }
            return result;
        } finally {
            if (isCurrent()) {
                importApplyInFlightRef.current = false;
                commands.refreshSelectedOverview({ reason: 'portfolio_import' });
            }
        }
    }, [
        actualPortfolioVersion,
        requestPosSizingContextUpdate,
        autoRefreshEnabledRef,
        importApplyInFlightRef,
        posSizingSyncInFlightRef,
        markPendingRefreshFromLoadResult,
        refreshPriceDataRef,
        riskCommandsRef,
        setActualPortfolioVersion,
    ]);

    applyHandlerRef.current = handleImportApplied;
    useEffect(() => {
        const scope = { userUid };
        mountedScopeRef.current = scope;
        const handler = (event) => {
            if (event?.detail?.userUid && event.detail.userUid !== userUid) return;
            const completion = applyHandlerRef.current();
            event?.detail?.waitUntil?.(completion);
            void completion.catch((error) => {
                console.error('Actual portfolio saved; Position Sizing handoff failed:', error);
            });
        };
        window.addEventListener('portfolio-import-applied', handler);
        return () => {
            window.removeEventListener('portfolio-import-applied', handler);
            if (mountedScopeRef.current === scope) mountedScopeRef.current = null;
        };
    }, [userUid]);

    return {
        handleImportApplied,
    };
}
